import {
  Building2, Star, DollarSign, MapPin, Globe, Sparkles, Plane, ExternalLink,
  ThumbsUp, ThumbsDown, Briefcase, Target, Users,
} from "lucide-react";
import SourceLinksBlock from "./SourceLinksBlock";

/** Ensure a URL string has https:// prefix for safe external navigation */
function ensureUrl(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  return `https://${trimmed}`;
}

function MiniStat({ label, value, icon: Icon }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-center gap-1.5 text-[10px]">
      {Icon && <Icon className="w-3 h-3 text-white/20 shrink-0" />}
      <span className="text-white/30">{label}</span>
      <span className="text-white/60 font-medium">{value}</span>
    </div>
  );
}

export default function IntelligenceSummary({ sourceProfiles, hotel }) {
  const rawAI = sourceProfiles?.ai_enrichment;
  const rawOTA = sourceProfiles?.ota_data;
  if ((!rawAI || Object.keys(rawAI).length === 0) && (!rawOTA || Object.keys(rawOTA).length === 0)) return null;

  const ai = rawAI || {};
  const ota = rawOTA || {};

  const structure = ai.hotel_structure || {};
  const facilities = ai.facilities || {};
  const location = ai.location_intelligence || ai.location || {};
  const reviewIntel = ota.review_intelligence || {};
  const priceRaw = ota.price_intelligence || {};
  const mice = ai.mice_intelligence || {};
  const biz = ai.business_intelligence || {};

  // Normalize OTA sources from old/new format
  const otaSources = (ota.ota_sources || (ota.sources_found || []).map(s => ({
    source_name: s.source_name || s.source || "",
    property_url: s.property_url || s.source_url || s.estimated_url || "",
    confidence: s.confidence || 0,
  }))).filter(s => s.confidence >= 0.3);

  // Build facility highlights
  const facilityHighlights = [
    facilities.spa && "Spa",
    facilities.swimming_pool && "Pool",
    facilities.fitness_center && "Fitness",
    facilities.restaurants && `${facilities.restaurants} Rest.`,
    facilities.bars && `${facilities.bars} Bars`,
    facilities.meeting_rooms && `${facilities.meeting_rooms} Meetings`,
    facilities.airport_transfer && "Transfer",
    facilities.wifi && "WiFi",
  ].filter(Boolean);

  // Ratings
  const bookingRating = reviewIntel.booking_rating || ota.rating_estimate;
  const ratings = [
    bookingRating && `Booking ${bookingRating}/10`,
    reviewIntel.google_rating && `Google ${reviewIntel.google_rating}/5`,
    reviewIntel.tripadvisor_rating && `TA ${reviewIntel.tripadvisor_rating}/5`,
  ].filter(Boolean);

  // Currency-normalized price display
  const priceCurrency = priceRaw.price_currency || priceRaw.currency || "USD";
  const priceValue = priceRaw.price_value || priceRaw.average_price_signal;
  let priceDisplay = null;
  if (priceValue) {
    priceDisplay = `~${priceCurrency} ${priceValue}/N`;
  } else if (ota.price_range && typeof ota.price_range === "string") {
    priceDisplay = ota.price_range;
  }

  // OTA amenities from old/new
  const amenities = ota.amenities_extracted || ota.amenities || [];

  return (
    <div className="bg-purple-500/[0.03] rounded-lg border border-purple-500/10 p-3 space-y-2" data-testid="intelligence-summary">
      <div className="flex items-center justify-between">
        <p className="text-purple-400/60 text-[10px] uppercase tracking-wider font-medium flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> AI Intelligence
        </p>
        {ai.model && <span className="text-[8px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400/40">{ai.model}</span>}
      </div>

      {/* Source Links — same block as Database */}
      <SourceLinksBlock sourceProfiles={sourceProfiles} hotel={hotel} />

      {/* Business Intelligence — Sales-focused compact */}
      {(biz.hotel_category || biz.sales_angle || biz.partnership_potential) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="summary-biz">
          {biz.hotel_category && <span className="text-[10px] text-white/50 font-medium">{biz.hotel_category}</span>}
          {biz.partnership_potential && (
            <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-semibold ${
              biz.partnership_potential === "high" ? "bg-green-500/15 text-green-400/70" :
              biz.partnership_potential === "medium" ? "bg-amber-500/12 text-amber-400/60" :
              "bg-white/[0.04] text-white/30"
            }`}>{biz.partnership_potential.toUpperCase()}</span>
          )}
          {biz.sales_angle && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400/50">{biz.sales_angle}</span>}
        </div>
      )}

      {/* Soulful Story compact */}
      {ai.summary?.soulful_story && (
        <p className="text-white/40 text-[11px] italic leading-relaxed" data-testid="summary-soulful-story" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          "{ai.summary.soulful_story}"
        </p>
      )}

      {/* Quick stats grid */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        <MiniStat icon={Building2} label="Zimmer" value={structure.total_rooms} />
        {structure.suites != null && <MiniStat label="Suiten" value={structure.suites} />}
        {structure.property_type && <MiniStat label="Typ" value={structure.property_type} />}
        {mice.mice_capability_score > 0 && (
          <MiniStat icon={Briefcase} label="MICE" value={
            mice.mice_capability_score >= 3 ? "Voll MICE" :
            mice.mice_capability_score >= 2 ? "Mittel" : "Klein"
          } />
        )}
      </div>

      {/* Facilities highlights */}
      {facilityHighlights.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="summary-facilities">
          {facilityHighlights.slice(0, 6).map(f => (
            <span key={f} className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400/50">{f}</span>
          ))}
        </div>
      )}

      {/* OTA amenities fallback */}
      {facilityHighlights.length === 0 && amenities.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="summary-amenities">
          {amenities.slice(0, 6).map(a => (
            <span key={a} className="text-[9px] px-1.5 py-0.5 rounded bg-teal-500/10 text-teal-400/50">{a}</span>
          ))}
        </div>
      )}

      {/* OTA Presence */}
      {otaSources.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="summary-ota">
          {otaSources.map(s => (
            <span key={s.source_name} className="text-[9px] px-1.5 py-0.5 rounded bg-teal-500/10 text-teal-400/50 capitalize">{s.source_name}</span>
          ))}
        </div>
      )}

      {/* Ratings + Price + Positioning */}
      <div className="flex flex-wrap gap-2 text-[10px]">
        {ratings.map(r => (
          <span key={r} className="flex items-center gap-1 text-white/35">
            <Star className="w-2.5 h-2.5 text-amber-400/40" />{r}
          </span>
        ))}
        {priceDisplay && (
          <span className="flex items-center gap-1 text-white/35">
            <DollarSign className="w-2.5 h-2.5 text-green-400/40" />
            {priceDisplay}
          </span>
        )}
        {priceRaw.price_positioning && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
            priceRaw.price_positioning === "ultra_luxury" ? "bg-amber-500/12 text-amber-400/60" :
            priceRaw.price_positioning === "upper_luxury" ? "bg-purple-500/12 text-purple-400/50" :
            priceRaw.price_positioning === "upscale" ? "bg-blue-500/10 text-blue-400/50" :
            "bg-white/[0.04] text-white/30"
          }`} data-testid="summary-price-positioning">
            {priceRaw.price_positioning.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
          </span>
        )}
      </div>

      {/* Review Snapshot — compact positive/negative */}
      {((reviewIntel.top_positive_points || []).length > 0 || (reviewIntel.top_negative_points || []).length > 0) && (
        <div className="flex flex-wrap gap-1.5" data-testid="summary-review-snapshot">
          {(reviewIntel.top_positive_points || []).slice(0, 3).map((p, i) => (
            <span key={`p${i}`} className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-400/50 flex items-center gap-0.5">
              <ThumbsUp className="w-2 h-2" />{p}
            </span>
          ))}
          {(reviewIntel.top_negative_points || []).slice(0, 2).map((n, i) => (
            <span key={`n${i}`} className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/8 text-red-400/40 flex items-center gap-0.5">
              <ThumbsDown className="w-2 h-2" />{n}
            </span>
          ))}
        </div>
      )}

      {/* Target guests */}
      {(biz.target_guest_profile || []).length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="summary-target-guests">
          <Users className="w-2.5 h-2.5 text-white/20" />
          {biz.target_guest_profile.map((t, i) => (
            <span key={i} className="text-[8px] px-1 py-0.5 rounded bg-indigo-500/10 text-indigo-400/50 capitalize">{t}</span>
          ))}
        </div>
      )}

      {/* Location */}
      {(location.nearest_airport || location.google_maps_link || location.destination_type || location.region) && (
        <div className="flex flex-wrap gap-2 text-[10px]">
          {location.destination_type && (
            <span className="flex items-center gap-1 text-white/30">
              <MapPin className="w-2.5 h-2.5" />{location.destination_type}
            </span>
          )}
          {!location.destination_type && location.region && (
            <span className="flex items-center gap-1 text-white/30">
              <MapPin className="w-2.5 h-2.5" />{location.region}
            </span>
          )}
          {location.nearest_airport && (
            <span className="flex items-center gap-1 text-white/30">
              <Plane className="w-2.5 h-2.5" />{location.nearest_airport}
              {location.airport_distance_km != null && ` (${location.airport_distance_km} km)`}
            </span>
          )}
          {location.google_maps_link && (
            <a href={ensureUrl(location.google_maps_link)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400/50 hover:text-blue-400/70">
              <Globe className="w-2.5 h-2.5" />Maps<ExternalLink className="w-2 h-2" />
            </a>
          )}
        </div>
      )}

      {/* Tags */}
      {((ai.positioning || []).length > 0 || (ai.experience_tags || []).length > 0) && (
        <div className="flex flex-wrap gap-1">
          {(ai.positioning || []).map(p => <span key={p} className="text-[8px] px-1 py-0.5 rounded bg-purple-500/10 text-purple-400/50 capitalize">{p}</span>)}
          {(ai.experience_tags || []).map(t => <span key={t} className="text-[8px] px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-400/50">{t}</span>)}
        </div>
      )}
    </div>
  );
}
